import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiSearch } from "react-icons/fi";
import ComponentLoader from "./ComponentLoader";

const AdminUserSwitcher = ({
  loading = false,
  managedUsers = [],
  onManagedUserChange,
  selectedManagedUserId,
  user,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  const options = useMemo(() => {
    const seen = new Set();
    const currentUserId = String(user?.id || "");
    const list = [
      {
        id: currentUserId,
        name: "My data",
        email: user?.email || "",
        employeeType: user?.employeeType || user?.role || "Admin",
        isCurrentUser: true,
        isActive: true,
      },
    ];

    seen.add(currentUserId);
    managedUsers.forEach((managedUser) => {
      const id = String(managedUser.id || "");
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      list.push({ ...managedUser, id });
    });

    return list;
  }, [managedUsers, user]);

  const selectedId = String(selectedManagedUserId || user?.id || "");
  const selectedUser = options.find((option) => option.id === selectedId) || options[0];
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = normalizedQuery
    ? options.filter((option) =>
        [option.name, option.email, option.employeeType]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery))
      )
    : options;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const handleSelect = (id) => {
    onManagedUserChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="new-hrm-admin-viewer" ref={wrapperRef}>
      <span>View as</span>
      <button
        aria-expanded={open}
        className="new-hrm-admin-viewer__button"
        type="button"
        onClick={() => setOpen((current) => !current)}
      >
        <strong>{selectedUser?.name || "Select employee"}</strong>
        <small>{selectedUser?.email || `${Math.max(options.length - 1, 0)} employees`}</small>
        <FiChevronDown aria-hidden="true" />
      </button>

      {open && (
        <div className="new-hrm-admin-viewer__menu">
          <label className="new-hrm-admin-viewer__search">
            <FiSearch aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${Math.max(options.length - 1, 0)} employees`}
            />
          </label>

          <div className="new-hrm-admin-viewer__list" role="listbox" aria-busy={loading}>
            <ComponentLoader active={loading} inline label="Loading employees" />

            {!loading && filteredOptions.map((option) => {
              const isSelected = option.id === selectedId;
              return (
                <button
                  aria-selected={isSelected}
                  className="new-hrm-admin-viewer__option"
                  key={option.id}
                  role="option"
                  type="button"
                  onClick={() => handleSelect(option.id)}
                >
                  <span>
                    <strong>{option.name}</strong>
                    <small>
                      {option.email}
                      {option.isActive === false ? " · inactive" : ""}
                    </small>
                  </span>
                  {isSelected && <FiCheck aria-hidden="true" />}
                </button>
              );
            })}

            {!loading && !filteredOptions.length && (
              <p className="new-hrm-admin-viewer__empty">No employees found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserSwitcher;
